'use client'
import { type FC, memo, useMemo } from 'react'
import { useParams, usePathname } from 'next/navigation'
import styled from 'styled-components'
import type { TypeCmsProjectCard, TypeCmsProjectCategory, TypeLang } from '@/schema'
import { mq, vw } from '@/styles'
import { Link } from './commons'
import { SectionNavLinks } from './SectionNavLinks'
import { CardProject } from './CardProject'

type Props = {
  data: {
    projects: TypeCmsProjectCategory['projects']
    categories: TypeCmsProjectCategory['categories']
  }
}

function getCategoryNamesFromIds(projectCategories: TypeCmsProjectCard['categories'], allCategories: TypeCmsProjectCategory['categories']): string {
  const names: string[] = []

  const traverseCategories = (categories: TypeCmsProjectCategory['categories']) => {
    for (const category of categories) {
      if (projectCategories.includes(category.id) && category.slug['en'] !== 'all') {
        names.push(category.name)
      }
      if (category.subcategories?.length) {
        traverseCategories(category.subcategories)
      }
    }
  }

  traverseCategories(allCategories)

  return names.join(', ')
}

const getLinks = (data: TypeCmsProjectCategory['categories'], lang, pathname) => data.map((item, idx) => {
  if(!item?.slug?.[lang]) return null
  const isActive = pathname.includes(item.slug[lang])

  return (
    <div key={idx}>
      <Link href={`/projects/${item.slug[lang]}`} className={isActive ? 'active':''}>{item.name}</Link>
    </div>
  )
})

export const MainProjectCategory:FC<Props> = memo(({ data }) => {
  const { lang }: { lang: TypeLang } = useParams()
  const { categories, projects } = data
  const pathname = usePathname()
  const CategoriesItems = useMemo(() => getLinks(categories, lang, pathname), [categories, lang, pathname])

  return (
    <Main>
      <SectionNavLinks title='Projects categories' slides={CategoriesItems} />
      <section className='s--projects'>
        <ul>
          {projects?.map((item, idx) => {
            return (
              <li key={idx}>
                <CardProject data={item} categoriesNames={getCategoryNamesFromIds(item.categories, categories)} isRelated={true}/>
              </li>
            )
          })}
        </ul>
      </section>
    </Main>
  )
})

const Main = styled.main`
 width: 100vw;
 width: 100dvw;
  section {
    &.s--projects {
      display: flex;
      justify-content: center;
    }

    /* &:not([class*='SectionContents__']) {
      margin-bottom: ${vw(60, 'mobile')};
    }

    &:first-of-type {
      margin-top: ${vw(60, 'mobile')};
    }


    ${mq.greaterThan('tablet')} {
      &:not([class*='SectionContents__']) {
        margin-bottom: ${vw(60, 'desktop')};
      }

      &:first-of-type {
        margin-top: ${vw(60, 'desktop')};
      }

    } */
  }

  ul {
    align-items: flex-start;
    display: flex;
    flex-wrap: wrap;
    gap: ${vw(23, 'mobile')};
    justify-content: center;
    margin: 0 auto;
    width: fit-content;

    ${mq.greaterThan('nexus7')} {
      gap: ${vw(23, 'tablet')};
    }

    ${mq.greaterThan('tablet')} {
      gap: ${vw(25, 'desktop')};
    }

    ${mq.greaterThan('desktop')} {
      gap: 25px;
    }

    li {
      width: 100%;

      ${mq.greaterThan('tablet')} {
        width: calc(50% - ${vw(25, 'tablet')});
      }
    }
  }
`